import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.png';

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const userMenuRef = useRef(null);

  const toggleMenu = () => setOpen(prev => !prev);

  const handleLinkClick = (e, id) => {
    setOpen(false);
    // If it's a section link (starts with # or is an id), handle scrolling
    if (id) {
      e.preventDefault();
      if (location.pathname !== '/') {
        navigate('/', { state: { scrollTo: id } });
      } else {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  // Handle scroll on mount if navigating from another page
  useEffect(() => {
    if (location.state && location.state.scrollTo) {
      const element = document.getElementById(location.state.scrollTo);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      // Clear state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    const sections = document.querySelectorAll('section[id]');
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.5 }
    );
    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [open]);

  // Click outside listener for user menu
  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userMenuRef]);

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate('/');
  };

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'benefits', label: 'Why Glaminant' },
    { id: 'services', label: 'Services' },
    { id: 'faq', label: 'FAQ' },
    { id: 'professionals', label: 'Join Us' },
    { id: 'contact', label: 'Contact' },
  ];

  return (
    <>
      {open && <div className="nav-overlay" onClick={() => setOpen(false)}></div>}
      <nav className="navbar">
        <div className="navbar-blur"></div>
        <div className="navbar-container">
          <Link to="/" className="logo-container">
            <img src={logo} alt="Glaminant logo" className="logo" />
            <span className="brand-name">Glaminant</span>
          </Link>

          <ul className={`nav-menu ${open ? 'open' : ''}`}>
            {navLinks.map(link => (
              <li className="nav-item" key={link.id}>
                <a
                  href={`#${link.id}`}
                  className={`nav-link ${activeSection === link.id ? 'active' : ''}`}
                  onClick={(e) => handleLinkClick(e, link.id)}
                >
                  {link.label}
                </a>
              </li>
            ))}

            {/* Mobile Auth Links */}
            <li className="nav-item mobile-only">
              {user ? (
                <div className="mobile-user-menu">
                  <div className="mobile-user-header">Hi, {user.name}</div>
                  <Link to="/dashboard" className="nav-link" onClick={() => setOpen(false)}>My Dashboard</Link>
                  <button className="nav-link logout-btn" onClick={handleLogout}>Log Out</button>
                </div>
              ) : (
                <div className="unified-auth-pod mobile-auth-buttons">
                  <Link to="/login" className="auth-segment login" onClick={() => setOpen(false)}>Log In</Link>
                  <div className="auth-divider"></div>
                  <Link to="/signup" className="auth-segment signup" onClick={() => setOpen(false)}>Sign Up</Link>
                </div>
              )}
            </li>
          </ul>

          {/* Desktop User Menu / Auth Buttons */}
          <div className="desktop-auth">
            {user ? (
              <div className="user-menu-container" ref={userMenuRef}>
                <button className="user-menu-trigger" onClick={() => setUserMenuOpen(!userMenuOpen)}>
                  Hi, {user.name.split(' ')[0]} ▾
                </button>
                {userMenuOpen && (
                  <div className="user-dropdown">
                    <Link to="/dashboard" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>My Dashboard</Link>
                    <button className="dropdown-item logout-item" onClick={handleLogout}>Log Out</button>
                  </div>
                )}
              </div>
            ) : (
              <div className="unified-auth-pod auth-buttons">
                <Link to="/login" className="auth-segment login">Log In</Link>
                <div className="auth-divider"></div>
                <Link to="/signup" className="auth-segment signup">Sign Up</Link>
              </div>
            )}
          </div>



          <button
            className={`nav-toggle ${open ? 'open' : ''}`}
            onClick={toggleMenu}
            aria-label="Toggle navigation"
          >
            <span className="hamburger"></span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default Navbar;